import { Moment } from 'moment';

import { RoleCapacity } from '@ng-gems/api';

import { Utils } from '@utils/utils';

import { AccessControlCapacity } from './account/access-control-capacity.model';
import { AccessControlGrant } from './account/access-control-grant.model';
import { AccessControlRole, RoleId } from './account/access-control-role.model';
import { AccessControlScope } from './account/access-control-scope.model';
import { Account } from './account/account.model';
import { Contract } from './account/contract.model';
import { NotificationSetting } from './account/notification-setting.model';
import { SiteGroup } from './account/site-group.model';
import { Site } from './account/site.model';
import { Supplier } from './account/supplier.model';
import { EmailSubscription } from './email-subscription.model';

/**
 * Model for API UsersContact Model *and* for CustomerSelfContact Model
 */
export class User {
  /**
   * Contact DT identifier.
   */
  id: number;
  /**
   * Outh API key.
   */
  apiKey: string;

  /**
   * Okta email that will allow the user to log in
   */
  email: string;

  /**
   * Contact first name.
   */
  firstname: string;

  /**
   * Contact last name.
   */
  lastname: string;

  /**
   * Contact position.
   */
  position: string;

  /**
   * Contact e-mail address.
   */
  emailSecondary: string;

  /**
   * Is an Engie user
   */
  isEngie: boolean;

  /**
   * Primary phone.
   */
  phone: string;

  /**
   * Secondary phone.
   */
  phoneSecondary: string;

  /**
   * If external, the accounts to which the user is associated.
   */
  accounts: Account[];

  /**
   * User has avatar
   */
  hasAvatar: boolean;

  /**
   * Avatar file
   */
  avatarFile: File;

  /**
   * Site group configured by current user
   */
  siteGroups: SiteGroup[];

  /**
   * Date of the last onBoard opening
   */
  onBoardDate: Moment;

  isActive: boolean;

  /**
   * Lang of the user
   */
  lang: string;

  /**
   *  User has sounds activated
   */
  hasSound: boolean;

  /**
   * Customer supplier
   */
  supplier: Supplier;

  /**
   * Notifications settings of the user
   */
  notificationsSettings: NotificationSetting[];

  /**
   * Email subscriptions
   */
  emailSubscriptions: EmailSubscription[];

  /**
   * User grants
   */
  accessControlGrants: AccessControlGrant[];

  /**
   * Preferred user countries
   */
  preferredCountries: string[];

  /**
   * Preferred user accounts
   */
  preferredAccounts: number[];

  /**
   * Terms of use acceptance
   */
  isTermsOfUseAccepted: boolean;

  /**
   * Terms of use acceptance date
   */
  termsOfUseAcceptanceDate: Moment;

  /**
   * List of countries allowed for the user
   */
  allowedCountries: string[];

  /**
   * List of contracts for which user receives emails
   */
  contractNotifications: string[] = [];

  /**
   * selected user accounts, used in profile editor component only
   */
  selectedAccounts: Account[];

  constructor(attributes: Partial<User> = {}) {
    Object.assign(this, attributes);
    this.selectedAccounts = (this.preferredAccounts || []).map((id: number) => new Account({ id }));
  }

  get allowedAccounts(): Account[] {
    const accounts = [];

    (this.accessControlGrants || []).forEach((grant: AccessControlGrant) => {
      if (grant.scope.account && !grant.scope.site) {
        accounts.push(grant.scope.account);
      }
    });

    return accounts;
  }

  get allowedSites(): Site[] {
    const sites = [];

    (this.accessControlGrants || []).forEach((grant: AccessControlGrant) => {
      if (grant.scope.site) {
        sites.push(grant.scope.site);
      }
    });

    return sites;
  }

  get hasAccounts(): boolean {
    return this.accounts?.length > 1 && (this.accounts || []).some((a: Account) => a.id !== undefined);
  }

  get countries() {
    return this.allowedCountries ? this.allowedCountries.join(', ') : null;
  }

  static sortFn = (a: User, b: User) => a.compare(b);

  private hasRoleIdForScope(role: RoleId, scope: AccessControlScope): boolean {
    return (this.accessControlGrants || []).some(
      (grant: AccessControlGrant) => grant.role.id === role && grant.scope.hasIntersectionWith(scope),
    );
  }

  hasRoleIdForContract(role: RoleId, contract: Contract): boolean {
    return this.hasRoleIdForScope(role, new AccessControlScope({ contract }));
  }

  hasRoleIdForAccount(roles: RoleId[], account: Account): boolean {
    return roles.some((role) => this.hasRoleIdForScope(role, new AccessControlScope({ account })));
  }

  hasCapacity(name: RoleCapacity, scope: AccessControlScope) {
    return (this.accessControlGrants || []).some(
      (grant: AccessControlGrant) => grant.scope.hasIntersectionWith(scope) && grant.role.hasCapacity(new AccessControlCapacity({ name })),
    );
  }

  hasAllCapacities(names: RoleCapacity[], scope: AccessControlScope) {
    return names.every((name: RoleCapacity) => this.hasCapacity(name, scope));
  }

  hasSomeCapacities(names: RoleCapacity[], scope: AccessControlScope) {
    return names.some((name: RoleCapacity) => this.hasCapacity(name, scope));
  }

  hasCapacityOnAccount(name: RoleCapacity, account: Account): boolean {
    return this.hasCapacity(name, new AccessControlScope({ account }));
  }

  hasSomeCapacitiesOnAccount(names: RoleCapacity[], account: Account): boolean {
    return this.hasSomeCapacities(names, new AccessControlScope({ account }));
  }

  canEditHiddenPageSettings() {
    const allowedRoleIds = [1, 177, 178, 181, 187, 189, 196, 236, 237, 239, 241, 242];
    return (this.accessControlGrants || []).some((grant: AccessControlGrant) => allowedRoleIds.includes(grant.role.id));
  }

  isLocal() {
    return (this.accessControlGrants || []).some((grant: AccessControlGrant) => grant.role.id === RoleId.local);
  }

  isSales() {
    return (this.accessControlGrants || []).some((grant: AccessControlGrant) => grant.role.id === RoleId.sales);
  }

  /**
   * Returns the full name of the User
   */
  toString(): string {
    return `${(this.firstname || '')[0]?.toUpperCase()}${this.firstname?.slice(1)} ${this.lastname?.toUpperCase()}`;
  }

  /**
   * Returns the name of the User cleaned and prefixed with an @
   */
  toMention(): string {
    return '@' + Utils.normalizeText(this.toString(), true, true, true);
  }

  clone(updates: Partial<User> = {}): User {
    return Object.assign(
      new User({
        id: this.id,
        email: this.email,
        firstname: this.firstname,
        lastname: this.lastname,
        position: this.position,
        emailSecondary: this.emailSecondary,
        isEngie: this.isEngie,
        phone: this.phone,
        phoneSecondary: this.phoneSecondary,
        accounts: this.accounts,
        hasAvatar: this.hasAvatar,
        siteGroups: this.siteGroups,
        onBoardDate: this.onBoardDate,
        lang: this.lang,
        hasSound: this.hasSound,
        notificationsSettings: this.notificationsSettings,
        emailSubscriptions: this.emailSubscriptions,
        supplier: this.supplier,
        accessControlGrants: this.accessControlGrants,
        preferredCountries: this.preferredCountries,
        preferredAccounts: this.preferredAccounts,
        isTermsOfUseAccepted: this.isTermsOfUseAccepted,
        termsOfUseAcceptanceDate: this.termsOfUseAcceptanceDate,
      }),
      updates,
    );
  }

  compare(user: User): number {
    const lastnameCompare = this.lastname.toLowerCase().localeCompare(user.lastname.toLowerCase());

    return lastnameCompare || this.firstname.toLowerCase().localeCompare(user.firstname.toLowerCase());
  }

  isInAccount(account: Account): boolean {
    return this.accounts && this.accounts.some((a: Account) => a.id === account.id);
  }

  getRoles(): AccessControlRole[] {
    return (this.accessControlGrants || []).map((grant: AccessControlGrant) => grant.role);
  }

  resolveRoles(roles: AccessControlRole[]) {
    (this.accessControlGrants || []).forEach((grant: AccessControlGrant) => {
      grant.role = roles.find((role: AccessControlRole) => grant.role.equalTo(role));
    });
    return this;
  }

  /**
   * A user can grant a role X to another user if he/she is already granted a role
   * that contains at least all the capacities of role X
   */
  canGrantRole(role: AccessControlRole): boolean {
    return (this.accessControlGrants || []).some((grant: AccessControlGrant) => grant.role.canGrant(role));
  }

  canSeeVolume(account: Account): boolean {
    return this.hasSomeCapacitiesOnAccount(
      [
        RoleCapacity.apiVolumesMeteringRead,
        RoleCapacity.apiVolumesNetworkRead,
        RoleCapacity.apiVolumesProfileRead,
        RoleCapacity.apiVolumesSubscriptionRead,
      ],
      account,
    );
  }

  canSeeInvoice(account: Account): boolean {
    return this.hasSomeCapacitiesOnAccount(
      [RoleCapacity.apiInvoiceOperateEngie, RoleCapacity.apiInvoicesDataRead, RoleCapacity.apiInvoiceRead],
      account,
    );
  }

  canSeeDocuments(account: Account): boolean {
    return this.hasCapacityOnAccount(RoleCapacity.apiPortalDocumentsRead, account);
  }

  canSeeCarbonDocuments(account: Account): boolean {
    return this.hasCapacityOnAccount(RoleCapacity.apiCarbonDocumentsRead, account);
  }

  canSeeCarbonReporting(account: Account): boolean {
    return this.hasCapacityOnAccount(RoleCapacity.apiCarbonReportingRead, account);
  }

  canOperateNomination(account: Account): boolean {
    return this.hasCapacityOnAccount(RoleCapacity.apiContractsNominationsOperateClient, account);
  }

  canReadNomination(account: Account): boolean {
    return this.hasCapacityOnAccount(RoleCapacity.apiContractsNominationsReadClient, account);
  }

  canOperateAuction(account: Account): boolean {
    return this.hasCapacityOnAccount(RoleCapacity.apiMidbackBiddingsOperateClient, account);
  }

  canReadAuction(account: Account): boolean {
    return this.hasCapacityOnAccount(RoleCapacity.apiMidbackBiddingsRead, account);
  }

  isNominatorOnContract(contract: Contract) {
    return this.hasRoleIdForContract(RoleId.nominator, contract);
  }

  isBidderOnContract(contract: Contract) {
    return this.hasRoleIdForContract(RoleId.conditionalBidder, contract);
  }

  isBidderBackupOnContract(contract: Contract) {
    return this.hasRoleIdForContract(RoleId.conditionalBidderBackup, contract);
  }

  isNominatorBackupOnContract(contract: Contract) {
    return this.hasRoleIdForContract(RoleId.nominatorBackup, contract);
  }

  isFullAdminOnContract(contract: Contract) {
    return this.hasRoleIdForContract(RoleId.fullAdmin, contract);
  }

  isConditionalBidder() {
    return (this.accessControlGrants || []).some((g) => g.role.id === RoleId.conditionalBidder);
  }

  isFullAdmin() {
    return (this.accessControlGrants || []).some((g) => g.role.id === RoleId.fullAdmin);
  }

  search(text: string) {
    const values1: string[] = []; // ${lastname} ${firstname} ${email}
    if (this.lastname) values1.push(this.lastname.toLowerCase());
    if (this.firstname) values1.push(this.firstname.toLowerCase());
    if (this.email) values1.push(this.email.toLowerCase());
    const values2: string[] = []; // ${firstname} ${lastname} ${email}
    if (this.firstname) values2.push(this.firstname.toLowerCase());
    if (this.lastname) values2.push(this.lastname.toLowerCase());
    if (this.email) values2.push(this.email.toLowerCase());
    return values1.join(' ').indexOf(text.toLowerCase()) > -1 || values2.join(' ').indexOf(text.toLowerCase()) > -1;
  }
}
